/**
 * Tableau API client.
 *
 * Two endpoints:
 *   GET  /api/tableau/dashboards  → list of embeddable dashboards (already
 *                                   filtered server-side to the caller's
 *                                   dashboard allowlist)
 *   POST /api/tableau/token       → fresh JWT for embedding ONE dashboard
 *
 * Auth: HttpOnly cookies (same as api.ts).
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

function jsonHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
  };
}

export interface TableauDashboard {
  id: string;
  name: string;
  description: string;
  owner: string;
  /**
   * Department folder the dashboard belongs to. One of:
   *   "sales" | "call_center" | "marketing" | "owner" | ""
   * Empty string means "unsorted" — the frontend renders these under an
   * "Other" fallback section rather than dropping them silently.
   *
   * Matches the `folder` field on DashboardInfo in
   * apps/api/ashi_api/api/tableau.py.
   */
  folder: string;
  content_url: string;
}

export interface TableauDashboardList {
  dashboards: TableauDashboard[];
  tableau_server: string;
  tableau_site: string;
}

export async function fetchTableauDashboards(): Promise<TableauDashboardList> {
  const response = await fetch(`${API_BASE}/api/tableau/dashboards`, {
    credentials: "include",
    headers: jsonHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to load dashboards: HTTP ${response.status}`);
  }
  return response.json();
}

export interface TableauTokenResponse {
  token: string;
  expires_in: number;
}

/**
 * Fetch an embedding token for a specific dashboard.
 *
 * `dashboardId` is REQUIRED: the server authorizes the request against the
 * caller's dashboard allowlist before signing, and returns 403 for a
 * dashboard the user isn't allowed to open. (The token is the real access
 * grant, since every embed authenticates as the same Tableau service account.)
 */
export async function fetchTableauToken(
  dashboardId: string,
): Promise<TableauTokenResponse> {
  const response = await fetch(`${API_BASE}/api/tableau/token`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ dashboard_id: dashboardId }),
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = `${detail}: ${body.detail}`;
    } catch {
      // ignore
    }
    throw new Error(`Failed to get Tableau token: ${detail}`);
  }
  return response.json();
}
